// ===========================================
// Role Editor Dialog
// ===========================================
// Create or edit a custom role with two-tier permission UI

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Switch,
  Stack,
  Chip,
  CircularProgress,
  Divider,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import ColorPickerField from '../../common/ColorPickerField';
import { PERMISSION_GROUPS, PermissionAction, RESOURCE_ACTION_MAP, PermissionResource } from '../../../types/permissions.types';
import { CustomRoleDetail, CreateCustomRoleInput, UpdateCustomRoleInput } from '../../../api/customRole.api';

// ===========================================
// TYPES
// ===========================================

interface RoleEditorDialogProps {
  open: boolean;
  role: CustomRoleDetail | null; // null = create mode
  onClose: () => void;
  onSave: (data: CreateCustomRoleInput | UpdateCustomRoleInput) => void;
  isSaving: boolean;
  /**
   * Edit mode: true while the role's FRESH detail is being fetched (pass the query's
   * `isFetching`, not `isLoading`: after a save the cached detail is stale but present).
   * The form seeds only once this is false (roles plan §5.8, F2).
   */
  isLoading?: boolean;
  /** Edit mode: the role's detail failed to load. Shows an error instead of an empty form. */
  loadError?: boolean;
}

// Convert the role's flat permissions array to a Set for quick lookup.
//
// ⚠️ Only grants the CURRENT resource map knows about are kept. A stored row for
// a resource that has since been removed (`website.*`, 2026-09-16) has no
// checkbox here, so it would be invisible, impossible to untick, and then
// re-sent on save — where the backend validator rejects the whole payload with
// "Invalid permission resource". Dropping it client-side keeps every existing
// role editable through any future resource removal.
export function buildPermissionSet(role: CustomRoleDetail | null): Set<string> {
  if (!role) return new Set();
  return new Set(
    role.Permissions
      .filter((p) => {
        const actions = RESOURCE_ACTION_MAP[p.resource as PermissionResource] as readonly string[] | undefined;
        return actions?.includes(p.action) ?? false;
      })
      .map((p) => `${p.resource}.${p.action}`)
  );
}

// ===========================================
// COMPONENT
// ===========================================

export default function RoleEditorDialog({
  open,
  role,
  onClose,
  onSave,
  isSaving,
  isLoading = false,
  loadError = false,
}: RoleEditorDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());

  // Seed the form ONCE PER OPEN, from fresh data (roles plan §5.8, F2). It used to reseed on
  // every change of `role`, so the editor opened with a stub (no permissions) and any later
  // refetch could wipe ticks the admin had just made. Now: wait until the detail has
  // fetched, seed, and ignore any refetch until the dialog is closed and reopened.
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm')); // full screen on a phone (#400)
  const seededRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current || isLoading || loadError) return;
    seededRef.current = true;
    setName(role?.name ?? '');
    setDescription(role?.description ?? '');
    setColor(role?.color ?? null);
    setSelectedPermissions(buildPermissionSet(role));
  }, [open, role, isLoading, loadError]);

  // ===========================================
  // PERMISSION HELPERS
  // ===========================================

  const isPermissionSelected = (resource: string, action: string): boolean =>
    selectedPermissions.has(`${resource}.${action}`);

  const togglePermission = (resource: string, action: string) => {
    const key = `${resource}.${action}`;
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  /** Count how many permissions are enabled for a group */
  const groupEnabledCount = (groupKey: string): number => {
    const group = PERMISSION_GROUPS.find((g) => g.key === groupKey);
    if (!group) return 0;
    return group.resources.reduce(
      (count, res) =>
        count + res.actions.filter((action) => isPermissionSelected(res.key, action)).length,
      0
    );
  };

  /** Total permissions in a group */
  const groupTotalCount = (groupKey: string): number => {
    const group = PERMISSION_GROUPS.find((g) => g.key === groupKey);
    if (!group) return 0;
    return group.resources.reduce((count, res) => count + res.actions.length, 0);
  };

  /** Toggle all permissions in a group */
  const toggleGroup = (groupKey: string, enable: boolean) => {
    const group = PERMISSION_GROUPS.find((g) => g.key === groupKey);
    if (!group) return;
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      for (const res of group.resources) {
        for (const action of res.actions) {
          const key = `${res.key}.${action}`;
          if (enable) {
            next.add(key);
          } else {
            next.delete(key);
          }
        }
      }
      return next;
    });
  };

  const isGroupFullyEnabled = (groupKey: string): boolean =>
    groupEnabledCount(groupKey) === groupTotalCount(groupKey);

  const isGroupPartiallyEnabled = (groupKey: string): boolean => {
    const count = groupEnabledCount(groupKey);
    return count > 0 && count < groupTotalCount(groupKey);
  };

  // ===========================================
  // SAVE HANDLER
  // ===========================================

  const handleSave = () => {
    // Convert Set back to array of {resource, action} objects
    const permissions = Array.from(selectedPermissions).map((perm) => {
      const [resource, action] = perm.split('.');
      return { resource, action };
    });

    // Editing: an emptied field is sent as null so it is CLEARED (#398); undefined means
    // "unchanged" to the server, which made Clear look saved while keeping the old value.
    const data: CreateCustomRoleInput | UpdateCustomRoleInput = role
      ? {
          name: name.trim(),
          description: description.trim() || null,
          color: color || null,
          permissions,
        }
      : {
          name: name.trim(),
          description: description.trim() || undefined,
          color: color || undefined,
          permissions,
        };

    onSave(data);
  };

  const canSave =
    name.trim().length > 0 &&
    selectedPermissions.size > 0 &&
    !isSaving &&
    !isLoading &&
    !loadError;

  // ===========================================
  // RENDER
  // ===========================================

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isPhone}
      PaperProps={{ sx: isPhone ? {} : { maxHeight: '90vh' } }}
    >
      <DialogTitle>
        {role ? `Edit Role: ${role.name}` : isLoading || loadError ? 'Edit Role' : 'Create Custom Role'}
        {role?.isSystem && (
          <Chip
            label="System"
            size="small"
            sx={{ ml: 1, bgcolor: '#FFCE00', color: '#080808', fontWeight: 600 }}
          />
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {loadError ? (
          <Alert severity="error">
            This role could not be loaded. Close this window and try again.
          </Alert>
        ) : isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 6 }}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">Loading the role's permissions…</Typography>
          </Box>
        ) : (
        <Stack spacing={3}>
          {/* Name */}
          <TextField
            label="Role Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
            disabled={isSaving}
            inputProps={{ maxLength: 50 }} // the server's cap (#398)
          />

          {/* Description */}
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            disabled={isSaving}
            inputProps={{ maxLength: 200 }} // the server's cap (#398)
            placeholder="Describe what this role is for..."
          />

          {/* Color */}
          <ColorPickerField
            label="Role Color (optional)"
            value={color}
            onChange={setColor}
            showClear
          />

          <Divider />

          {/* Permissions Section */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Permissions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select which actions this role can perform. Use the master toggle to
              enable or disable an entire group at once.
            </Typography>

            {/* A system role (roles slice 3) is school policy to edit, and can go back to its default. */}
            {role?.defaultPermissions && (
              <Box sx={{ mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isSaving}
                  onClick={() =>
                    setSelectedPermissions(
                      buildPermissionSet({ ...role, Permissions: role.defaultPermissions!.map((p) => ({ id: '', ...p })) })
                    )
                  }
                >
                  Reset to default
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Ticks the permissions this role starts with. Nothing changes until you save.
                </Typography>
              </Box>
            )}

            {selectedPermissions.size === 0 && (
              <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                At least one permission must be selected.
              </Typography>
            )}

            <Stack spacing={1}>
              {PERMISSION_GROUPS.map((group) => {
                const enabledCount = groupEnabledCount(group.key);
                const totalCount = groupTotalCount(group.key);
                const fullyEnabled = isGroupFullyEnabled(group.key);
                const partiallyEnabled = isGroupPartiallyEnabled(group.key);

                return (
                  <Accordion
                    key={group.key}
                    disableGutters
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: enabledCount > 0 ? 'primary.light' : 'divider',
                      borderRadius: 1,
                      '&:before': { display: 'none' },
                      bgcolor: enabledCount > 0 ? 'rgba(69, 128, 228, 0.04)' : 'background.paper',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mr: 1,
                        },
                      }}
                    >
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          {group.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {group.description}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {enabledCount > 0 && (
                          <Chip
                            label={`${enabledCount}/${totalCount}`}
                            size="small"
                            sx={{
                              bgcolor: '#4580E4',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                        )}
                        <FormControlLabel
                          label=""
                          onClick={(e) => e.stopPropagation()}
                          control={
                            <Switch
                              checked={fullyEnabled || partiallyEnabled}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleGroup(group.key, e.target.checked);
                              }}
                              size="small"
                              color={partiallyEnabled && !fullyEnabled ? 'warning' : 'primary'}
                            />
                          }
                        />
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                      <Stack spacing={1}>
                        {group.resources.map((resource) => (
                          <Box
                            key={resource.key}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              // On a phone the label takes its own line above the actions (#400).
                              flexWrap: { xs: 'wrap', sm: 'nowrap' },
                              gap: 1,
                              py: 0.5,
                              px: 1,
                              borderRadius: 1,
                              bgcolor: 'background.default',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ minWidth: { xs: '100%', sm: 200 }, flexShrink: 0 }}
                            >
                              {resource.label}
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {resource.actions.map((action: PermissionAction) => (
                                <FormControlLabel
                                  key={action}
                                  label={
                                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                                      {action}
                                    </Typography>
                                  }
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={isPermissionSelected(resource.key, action)}
                                      onChange={() => togglePermission(resource.key, action)}
                                      disabled={isSaving}
                                      sx={{ py: 0.25, px: 0.5 }}
                                    />
                                  }
                                  sx={{ mr: 1 }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          </Box>
        </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
        >
          {role || isLoading || loadError ? 'Save Changes' : 'Create Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
